import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { BoardSnapshot } from "@business-ops/domain";
import {
  AtSign,
  Building2,
  CheckCircle2,
  CircleAlert,
  LoaderCircle,
  MessageCircleMore,
  Plus,
  RefreshCw,
  ShieldCheck,
  Unplug,
  UsersRound
} from "lucide-react";
import {
  productApi,
  type ProductApi,
  type ProductBusiness,
  type ProductDepartment,
  type ProductDepartmentAccess,
  type ProductProviderConnection,
  type ProductSession
} from "../product-api.js";
import { businessSlugFromName } from "./product-onboarding.js";
import { ProductSubscriptionPanel } from "./product-subscription-panel.js";

type WorkspaceMember = Pick<BoardSnapshot["members"][number], "id" | "displayName" | "role">;
type RelevantProvider = "WHATSAPP_BAILEYS" | "WHATSAPP_CLOUD" | "X";
type LoadState = "LOADING" | "READY" | "LEGACY_ONLY" | "ERROR";

interface ProductSettingsSnapshot {
  session: ProductSession;
  businesses: readonly ProductBusiness[];
  connections: readonly ProductProviderConnection[];
}

interface DepartmentAttempt {
  department: ProductDepartment;
  memberId: string;
  memberName: string;
  accessScope: ProductDepartmentAccess["accessScope"];
  accessLevel: ProductDepartmentAccess["accessLevel"];
  access: ProductDepartmentAccess | null;
}

export function ProductWorkspaceSettings({
  workspaceId,
  members,
  canConfigure,
  onSwitchBusiness,
  apiClient = productApi
}: {
  workspaceId: string;
  members: readonly WorkspaceMember[];
  canConfigure: boolean;
  onSwitchBusiness: (businessSlug: string) => void;
  apiClient?: ProductApi;
}) {
  const [loadState, setLoadState] = useState<LoadState>("LOADING");
  const [snapshot, setSnapshot] = useState<ProductSettingsSnapshot | null>(null);
  const [loadError, setLoadError] = useState("");
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const [actionStatus, setActionStatus] = useState("");
  const [businessSlugEdited, setBusinessSlugEdited] = useState(false);
  const [businessDraft, setBusinessDraft] = useState({ name: "", slug: "", timezone: "UTC" });
  const [connectionDraft, setConnectionDraft] = useState<{
    provider: RelevantProvider;
    displayName: string;
    externalAccountRef: string;
  }>({ provider: "WHATSAPP_BAILEYS", displayName: "", externalAccountRef: "" });
  const [departmentDraft, setDepartmentDraft] = useState<{
    name: string;
    description: string;
    memberId: string;
    accessScope: ProductDepartmentAccess["accessScope"];
    accessLevel: ProductDepartmentAccess["accessLevel"];
  }>({
    name: "",
    description: "",
    memberId: members[0]?.id ?? "",
    accessScope: "ASSIGNED_RECORDS",
    accessLevel: "CONTRIBUTE"
  });
  const [departmentAttempt, setDepartmentAttempt] = useState<DepartmentAttempt | null>(null);

  const load = useCallback(async () => {
    setLoadState("LOADING");
    setLoadError("");
    try {
      const [session, businessesResult, connectionsResult] = await Promise.all([
        apiClient.session(),
        apiClient.businesses(),
        apiClient.connections()
      ]);
      // WHY [BOL-PRODUCT-SETTINGS-TENANT-001]: department member IDs and
      // provider records are safe only inside the exact operations workspace
      // already judged by the shell. Never render or mutate product data when
      // the two session projections disagree. Falsifier: Settings shows or
      // changes another business while the shell remains on this board.
      if (session.business.id !== workspaceId) {
        setLoadError("The business account and operations workspace do not match. Sign in again before changing settings.");
        setLoadState("ERROR");
        return;
      }
      setSnapshot({
        session,
        businesses: businessesResult.businesses,
        connections: connectionsResult.connections.filter(isRelevantConnection)
      });
      setBusinessDraft((current) => ({ ...current, timezone: session.business.timezone }));
      setLoadState("READY");
    } catch (cause) {
      if (requestStatus(cause) === 401) {
        setLoadState("LEGACY_ONLY");
        return;
      }
      setLoadError(errorMessage(cause, "Business account settings could not be loaded."));
      setLoadState("ERROR");
    }
  }, [apiClient, workspaceId]);

  useEffect(() => { void load(); }, [load]);

  const clearActionMessages = () => {
    setActionError("");
    setActionStatus("");
  };

  const createBusiness = async (event: FormEvent) => {
    event.preventDefault();
    clearActionMessages();
    setBusyAction("business");
    try {
      const result = await apiClient.createBusiness({
        name: businessDraft.name.trim(),
        slug: businessDraft.slug.trim(),
        timezone: businessDraft.timezone.trim()
      });
      setSnapshot((current) => current ? {
        ...current,
        businesses: upsertBusiness(current.businesses, result.business)
      } : current);
      setBusinessDraft((current) => ({ name: "", slug: "", timezone: current.timezone }));
      setBusinessSlugEdited(false);
      setActionStatus(`${result.business.name} was created. Sign in to open its separate workspace.`);
    } catch (cause) {
      setActionError(errorMessage(cause, "The business could not be created."));
    } finally {
      setBusyAction(null);
    }
  };

  const registerConnection = async (event: FormEvent) => {
    event.preventDefault();
    clearActionMessages();
    setBusyAction("connection");
    try {
      // WHY [BOL-PROVIDER-RECORD-UI-001]: this browser has no provider OAuth,
      // callback, or secret-vault authority. Save only the typed account record
      // and render the exact server status; the existing WhatsApp link/resync
      // surface remains separate. Falsifier: Save appears connected, asks for a
      // secret handle, or starts provider/device work.
      const result = await apiClient.registerConnection({
        provider: connectionDraft.provider,
        displayName: connectionDraft.displayName.trim(),
        externalAccountRef: connectionDraft.externalAccountRef.trim(),
        scopes: []
      });
      setSnapshot((current) => current ? {
        ...current,
        connections: upsertConnection(current.connections, result.connection)
      } : current);
      setConnectionDraft((current) => ({ ...current, displayName: "", externalAccountRef: "" }));
      setActionStatus(`${result.connection.displayName} was saved as ${connectionStatusLabel(result.connection.status)}. No provider authorization was added by this screen.`);
    } catch (cause) {
      setActionError(errorMessage(cause, "The connection record could not be saved."));
    } finally {
      setBusyAction(null);
    }
  };

  const revokeConnection = async (connection: ProductProviderConnection) => {
    if (!window.confirm(`Revoke ${connection.displayName} in Business Ops? This stops future operations through this record. It does not sign out of WhatsApp or X.`)) return;
    clearActionMessages();
    setBusyAction(`revoke:${connection.id}`);
    try {
      const result = await apiClient.revokeConnection(connection.id, connection.version);
      setSnapshot((current) => current ? {
        ...current,
        connections: upsertConnection(current.connections, result.connection)
      } : current);
      setActionStatus(`${result.connection.displayName} was revoked in Business Ops.`);
    } catch (cause) {
      setActionError(errorMessage(cause, "The connection changed and could not be revoked."));
      void load();
    } finally {
      setBusyAction(null);
    }
  };

  const assignDepartmentAccess = async (attempt: DepartmentAttempt) => {
    const result = await apiClient.setDepartmentAccess({
      memberId: attempt.memberId,
      departmentId: attempt.department.id,
      accessScope: attempt.accessScope,
      accessLevel: attempt.accessLevel,
      expectedVersion: null
    });
    const completed = { ...attempt, access: result.access };
    setDepartmentAttempt(completed);
    setActionStatus(`${attempt.department.name} is saved. ${attempt.memberName} now has ${accessScopeLabel(attempt.accessScope).toLowerCase()} with ${accessLevelLabel(attempt.accessLevel).toLowerCase()} access.`);
  };

  const createDepartment = async (event: FormEvent) => {
    event.preventDefault();
    clearActionMessages();
    setBusyAction("department");
    let attempt: DepartmentAttempt | null = null;
    try {
      const member = members.find((candidate) => candidate.id === departmentDraft.memberId);
      if (!member) throw new Error("Choose a current team member.");
      const result = await apiClient.createDepartment({
        name: departmentDraft.name.trim(),
        description: departmentDraft.description.trim()
      });
      attempt = {
        department: result.department,
        memberId: member.id,
        memberName: member.displayName,
        accessScope: departmentDraft.accessScope,
        accessLevel: departmentDraft.accessLevel,
        access: null
      };
      setDepartmentAttempt(attempt);
      await assignDepartmentAccess(attempt);
      setDepartmentDraft((current) => ({ ...current, name: "", description: "" }));
    } catch (cause) {
      setActionError(attempt
        ? `${attempt.department.name} is saved, but access could not be confirmed. ${errorMessage(cause, "Try the access assignment again.")}`
        : errorMessage(cause, "The department could not be created."));
    } finally {
      setBusyAction(null);
    }
  };

  const retryDepartmentAccess = async () => {
    if (!departmentAttempt || departmentAttempt.access) return;
    clearActionMessages();
    setBusyAction("department-access");
    try {
      await assignDepartmentAccess(departmentAttempt);
    } catch (cause) {
      setActionError(errorMessage(cause, "The department exists, but its access rule still could not be assigned."));
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <section className="product-workspace-settings" aria-labelledby="product-settings-title">
      <header className="product-settings-header">
        <span className="product-settings-header__icon"><Building2 size={21} aria-hidden="true" /></span>
        <div><p className="eyebrow">Business account</p><h2 id="product-settings-title">Businesses and access</h2><p>Keep each business, provider account, and department boundary explicit.</p></div>
        {loadState !== "LEGACY_ONLY" ? <button className="product-refresh-button" type="button" disabled={loadState === "LOADING"} onClick={() => void load()} aria-label="Refresh business account settings"><RefreshCw className={loadState === "LOADING" ? "spin" : ""} size={17} aria-hidden="true" /></button> : null}
      </header>

      {loadState === "LOADING" ? <p className="product-settings-state"><LoaderCircle className="spin" size={18} aria-hidden="true" />Loading business account settings.</p> : null}
      {loadState === "LEGACY_ONLY" ? <div className="product-settings-state product-settings-state--notice"><ShieldCheck size={19} aria-hidden="true" /><div><strong>Existing workspace sign-in</strong><p>Business account tools are not available for this sign-in. Sign out, then use an email-based business account to manage businesses and connection records.</p></div></div> : null}
      {loadState === "ERROR" ? <div className="product-settings-state product-settings-state--error" role="alert"><CircleAlert size={19} aria-hidden="true" /><div><strong>Business settings unavailable</strong><p>{loadError}</p></div><button className="secondary-button" type="button" onClick={() => void load()}>Try again</button></div> : null}

      {loadState === "READY" && snapshot ? <>
        <div className="product-current-business">
          <span><Building2 size={20} aria-hidden="true" /></span>
          <div><small>Current business</small><strong>{snapshot.session.business.name}</strong><p>{snapshot.session.business.slug} · {snapshot.session.business.timezone}</p></div>
          <span className="product-role-badge"><ShieldCheck size={14} aria-hidden="true" />{roleLabel(snapshot.session.member.role)}</span>
        </div>

        <div className="product-settings-layout">
          <section className="product-settings-panel" aria-labelledby="businesses-title">
            <header><div><p className="eyebrow">Account</p><h3 id="businesses-title">Your businesses</h3></div><span>{snapshot.businesses.length}</span></header>
            <div className="product-business-list">{snapshot.businesses.map((business) => {
              const current = business.id === snapshot.session.business.id;
              return <article key={business.id} className={current ? "is-current" : ""}>
                <div><strong>{business.name}</strong><small>{business.slug} · {roleLabel(business.role)}</small></div>
                {current ? <span aria-current="true">Open now</span> : <button className="secondary-button" type="button" onClick={() => {
                  if (window.confirm(`Open ${business.name}? You will need to sign in again so the workspace stays isolated.`)) onSwitchBusiness(business.slug);
                }}>Sign in to open</button>}
              </article>;
            })}</div>
            {snapshot.session.member.role === "OWNER" ? <details className="product-settings-disclosure">
              <summary><Plus size={17} aria-hidden="true" />Create another business</summary>
              <form className="product-settings-form" onSubmit={(event) => void createBusiness(event)}>
                <label><span>Business name</span><input type="text" required maxLength={160} autoComplete="organization" value={businessDraft.name} onChange={(event) => {
                  const name = event.target.value;
                  setBusinessDraft((current) => ({ ...current, name, slug: businessSlugEdited ? current.slug : businessSlugFromName(name) }));
                }} /></label>
                <label><span>Business slug</span><input type="text" required pattern="[a-z0-9][a-z0-9-]{1,62}" value={businessDraft.slug} onChange={(event) => { setBusinessSlugEdited(true); setBusinessDraft((current) => ({ ...current, slug: event.target.value.toLowerCase() })); }} /></label>
                <label><span>Timezone</span><input type="text" required maxLength={80} value={businessDraft.timezone} onChange={(event) => setBusinessDraft((current) => ({ ...current, timezone: event.target.value }))} /></label>
                <button className="primary-button" type="submit" disabled={busyAction !== null}>{busyAction === "business" ? <><LoaderCircle className="spin" size={16} aria-hidden="true" />Creating…</> : "Create business"}</button>
              </form>
            </details> : <p className="product-settings-note">Only an owner can create another business.</p>}
          </section>

          <section className="product-settings-panel" aria-labelledby="connections-title">
            <header><div><p className="eyebrow">Provider records</p><h3 id="connections-title">WhatsApp and X</h3></div><span>{snapshot.connections.length}</span></header>
            <p className="product-settings-note">Device linking and message resync stay in the WhatsApp screen. This list records provider accounts and their exact availability.</p>
            {snapshot.connections.length === 0 ? <p className="product-settings-empty">No WhatsApp or X account records.</p> : <div className="product-connection-list">{snapshot.connections.map((connection) => <article key={connection.id}>
              <span className="product-connection-icon">{connection.provider === "X" ? <AtSign size={18} aria-hidden="true" /> : <MessageCircleMore size={18} aria-hidden="true" />}</span>
              <div><strong>{connection.displayName}</strong><small>{providerLabel(connection.provider)} · {connection.externalAccountRef}</small><p>{connectionAvailabilityCopy(connection)}</p></div>
              <span className={`product-connection-status product-connection-status--${connection.status.toLowerCase()}`}>{connectionStatusLabel(connection.status)}</span>
              {canConfigure && ["OWNER", "MANAGER"].includes(snapshot.session.member.role) && connection.status !== "REVOKED" ? <button className="product-revoke-button" type="button" disabled={busyAction !== null} onClick={() => void revokeConnection(connection)}>{busyAction === `revoke:${connection.id}` ? <LoaderCircle className="spin" size={15} aria-hidden="true" /> : <Unplug size={15} aria-hidden="true" />}<span>{busyAction === `revoke:${connection.id}` ? "Revoking" : "Revoke"}</span></button> : null}
            </article>)}</div>}
            {canConfigure && ["OWNER", "MANAGER"].includes(snapshot.session.member.role) ? <details className="product-settings-disclosure">
              <summary><Plus size={17} aria-hidden="true" />Add an account record</summary>
              <form className="product-settings-form" onSubmit={(event) => void registerConnection(event)}>
                <label><span>Provider</span><select value={connectionDraft.provider} onChange={(event) => setConnectionDraft((current) => ({ ...current, provider: event.target.value as RelevantProvider }))}><option value="WHATSAPP_BAILEYS">WhatsApp · linked device</option><option value="WHATSAPP_CLOUD">WhatsApp · Cloud API</option><option value="X">X</option></select></label>
                <label><span>Display name</span><input type="text" required maxLength={200} value={connectionDraft.displayName} onChange={(event) => setConnectionDraft((current) => ({ ...current, displayName: event.target.value }))} /></label>
                <label><span>Account reference</span><input type="text" required maxLength={240} aria-describedby="connection-reference-help" value={connectionDraft.externalAccountRef} onChange={(event) => setConnectionDraft((current) => ({ ...current, externalAccountRef: event.target.value }))} /><small id="connection-reference-help">Use the phone number, handle, or provider account ID your team recognizes.</small></label>
                <p className="product-form-notice"><CircleAlert size={16} aria-hidden="true" />This saves a record only. Provider sign-in is not available here, so a new record remains unavailable until it is configured server-side.</p>
                <button className="primary-button" type="submit" disabled={busyAction !== null}>{busyAction === "connection" ? <><LoaderCircle className="spin" size={16} aria-hidden="true" />Saving…</> : "Save account record"}</button>
              </form>
            </details> : null}
          </section>
        </div>

        <ProductSubscriptionPanel apiClient={apiClient} role={snapshot.session.member.role} businessSlug={snapshot.session.business.slug} canConfigure={canConfigure} />

        <section className="product-settings-panel product-department-panel" aria-labelledby="departments-title">
          <header><div><p className="eyebrow">Data boundaries</p><h3 id="departments-title">Roles and departments</h3></div><UsersRound size={20} aria-hidden="true" /></header>
          <p className="product-settings-note">A role controls what a person can do. Department access controls which records they can use; one never silently changes the other.</p>
          <div className="product-member-strip" aria-label="Current team roles">{members.map((member) => <span key={member.id}><strong>{member.displayName}</strong><small>{roleLabel(member.role)}</small></span>)}</div>
          {canConfigure && ["OWNER", "MANAGER"].includes(snapshot.session.member.role) ? <details className="product-settings-disclosure">
            <summary><Plus size={17} aria-hidden="true" />Create a department and assign access</summary>
            <form className="product-settings-form product-department-form" onSubmit={(event) => void createDepartment(event)}>
              <label><span>Department name</span><input type="text" required maxLength={160} value={departmentDraft.name} onChange={(event) => setDepartmentDraft((current) => ({ ...current, name: event.target.value }))} /></label>
              <label><span>Description</span><input type="text" maxLength={2000} value={departmentDraft.description} onChange={(event) => setDepartmentDraft((current) => ({ ...current, description: event.target.value }))} /></label>
              <label><span>Team member</span><select required value={departmentDraft.memberId} onChange={(event) => setDepartmentDraft((current) => ({ ...current, memberId: event.target.value }))}><option value="" disabled>Choose a team member</option>{members.map((member) => <option key={member.id} value={member.id}>{member.displayName} · {roleLabel(member.role)}</option>)}</select></label>
              <label><span>Record scope</span><select value={departmentDraft.accessScope} onChange={(event) => setDepartmentDraft((current) => ({ ...current, accessScope: event.target.value as ProductDepartmentAccess["accessScope"] }))}><option value="OWN_RECORDS">Own records</option><option value="ASSIGNED_RECORDS">Assigned records</option><option value="DEPARTMENT_RECORDS">Department records</option></select></label>
              <label><span>Access level</span><select value={departmentDraft.accessLevel} onChange={(event) => setDepartmentDraft((current) => ({ ...current, accessLevel: event.target.value as ProductDepartmentAccess["accessLevel"] }))}><option value="READ">Read</option><option value="CONTRIBUTE">Contribute</option><option value="MANAGE">Manage</option></select></label>
              <button className="primary-button" type="submit" disabled={busyAction !== null || members.length === 0}>{busyAction === "department" ? <><LoaderCircle className="spin" size={16} aria-hidden="true" />Creating…</> : "Create and assign"}</button>
            </form>
          </details> : null}
          {departmentAttempt ? <div className={`product-department-result ${departmentAttempt.access ? "is-complete" : "needs-access"}`}>
            {departmentAttempt.access ? <CheckCircle2 size={18} aria-hidden="true" /> : <CircleAlert size={18} aria-hidden="true" />}
            <div><strong>{departmentAttempt.department.name}</strong><p>{departmentAttempt.access ? `${departmentAttempt.memberName} · ${accessScopeLabel(departmentAttempt.accessScope)} · ${accessLevelLabel(departmentAttempt.accessLevel)}` : `Department saved. Access for ${departmentAttempt.memberName} could not be confirmed.`}</p></div>
            {!departmentAttempt.access ? <button className="secondary-button" type="button" disabled={busyAction !== null} onClick={() => void retryDepartmentAccess()}>{busyAction === "department-access" ? <LoaderCircle className="spin" size={15} aria-hidden="true" /> : null}{busyAction === "department-access" ? "Assigning" : "Retry access"}</button> : null}
          </div> : null}
        </section>

        {actionError ? <p className="product-action-message product-action-message--error" role="alert">{actionError}</p> : null}
        {actionStatus ? <p className="product-action-message product-action-message--success" role="status"><CheckCircle2 size={17} aria-hidden="true" />{actionStatus}</p> : null}
      </> : null}
    </section>
  );
}

export function connectionStatusLabel(status: ProductProviderConnection["status"]): string {
  return status === "CONNECTED" ? "Connected"
    : status === "PENDING" ? "Pending verification"
      : status === "REVOKED" ? "Revoked"
        : "Unavailable";
}

export function connectionAvailabilityCopy(connection: Pick<ProductProviderConnection, "status" | "unavailableReason" | "credentialConfigured">): string {
  if (connection.status === "REVOKED") return "Revoked in Business Ops. The external account sign-in is unchanged.";
  if (connection.status === "CONNECTED") return "The server reports this provider account as connected.";
  if (connection.status === "PENDING") return "Provider authorization is awaiting verification.";
  if (connection.unavailableReason === "CREDENTIAL_REF_REQUIRED" || !connection.credentialConfigured) {
    return "Provider authorization has not been configured.";
  }
  return "Provider setup is unavailable for this record.";
}

export function roleLabel(role: ProductBusiness["role"] | WorkspaceMember["role"]): string {
  return role.charAt(0) + role.slice(1).toLowerCase();
}

function providerLabel(provider: ProductProviderConnection["provider"]): string {
  return provider === "X" ? "X" : provider === "WHATSAPP_CLOUD" ? "WhatsApp Cloud API" : "WhatsApp linked device";
}

function accessScopeLabel(scope: ProductDepartmentAccess["accessScope"]): string {
  return scope === "OWN_RECORDS" ? "Own records" : scope === "ASSIGNED_RECORDS" ? "Assigned records" : "Department records";
}

function accessLevelLabel(level: ProductDepartmentAccess["accessLevel"]): string {
  return level.charAt(0) + level.slice(1).toLowerCase();
}

function isRelevantConnection(connection: ProductProviderConnection): boolean {
  return connection.provider === "WHATSAPP_BAILEYS" || connection.provider === "WHATSAPP_CLOUD" || connection.provider === "X";
}

function upsertBusiness(businesses: readonly ProductBusiness[], business: ProductBusiness): readonly ProductBusiness[] {
  return [...businesses.filter((candidate) => candidate.id !== business.id), business]
    .sort((left, right) => left.name.localeCompare(right.name));
}

function upsertConnection(connections: readonly ProductProviderConnection[], connection: ProductProviderConnection): readonly ProductProviderConnection[] {
  return [...connections.filter((candidate) => candidate.id !== connection.id), connection]
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
}

function requestStatus(value: unknown): number | undefined {
  return typeof value === "object" && value !== null && "status" in value && typeof value.status === "number" ? value.status : undefined;
}

function errorMessage(value: unknown, fallback: string): string {
  return value instanceof Error && value.message ? value.message : fallback;
}
